import React, { useCallback } from 'react';
import i18next from 'i18next';
import useIsInViewport from '../../Hooks/useIsInViewport';

import {
  Wrapper,
  Image,
  Picture,
  Source,
  Img,
  Content,
  ContentBody,
  ContentFooter,
  Heading,
  PreHeading,
  Link,
  Description,
  Usp,
  Price,
  CurrentPrice,
  OldPrice,
  PriceCell,
  Label,
} from '../../StyledComponents/ProductCard/wrapper';
import UspList, { ItemProps } from '../../StyledComponents/UspList';
import { DEFAULT_PLACEHOLDER_IMAGE } from '../../utils/constants';
import { imageSrcSet, imageUrl } from '../../utils/image';
import { PRODUCT_CARD_IMAGE_SPECS } from './utils';

interface Props {
  id: string;
  title: string;
  href?: string;
  slug: string;
  image?: string;
  placeholderImage?: string;
  description?: string;
  uspList?: ItemProps[];
  price: string;
  leasingPrice?: string;
  oldPrice?: string;
  deductibleVat?: string;
  branchName?: string;
  headingLevel?: 1 | 2 | 3 | 4 | 5 | 6;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>, id: string, slug: string) => void;
}

const ProductCard = ({
  id,
  title,
  href,
  slug,
  image,
  placeholderImage,
  description,
  uspList,
  price,
  oldPrice,
  leasingPrice,
  deductibleVat,
  branchName,
  headingLevel = 2,
  onClick: _onClick,
}: Props) => {
  const [containerRef, isVisible] = useIsInViewport<HTMLDivElement>({
    persistVisibility: true,
  });

  const onClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      if (_onClick) {
        _onClick(e, id, slug);
      }
    },
    [_onClick, id]
  );

  return (
    <Wrapper>
      <Image ref={containerRef}>
        {isVisible && image ? (
          <>
            <Picture>
              <Source
                type="image/webp"
                srcSet={imageSrcSet(image, PRODUCT_CARD_IMAGE_SPECS, true)}
                sizes="(min-width: 600px) calc(((100vw - 48px) / 2) - 8px), (min-width: 900px) calc(((100vw - 48px) / 3) - 10.666px), (min-width: 1312px) 410px, calc(100vw - 32px)"
              />
              <Img
                srcSet={imageSrcSet(image, PRODUCT_CARD_IMAGE_SPECS)}
                sizes="(min-width: 600px) calc(((100vw - 48px) / 2) - 8px), (min-width: 900px) calc(((100vw - 48px) / 3) - 10.666px), (min-width: 1312px) 410px, calc(100vw - 32px)"
                src={imageUrl(image, { width: 411, height: 274 })}
                alt={`${title} ${description}`}
              />
            </Picture>
          </>
        ) : (
          <Picture>
            <Img src={placeholderImage || DEFAULT_PLACEHOLDER_IMAGE} alt={`${title} ${description}`} />
          </Picture>
        )}
      </Image>
      <Content>
        <ContentBody>
          {branchName && <PreHeading>{branchName}</PreHeading>}
          <Heading as={`h${headingLevel}`}>
            {href ? (
              <Link
                onClick={onClick}
                href={href ? `${href}${id}/${slug}` : undefined}
                title={title}
                aria-label={title}
              >
                {title}
              </Link>
            ) : (
              title
            )}
          </Heading>
          {description && <Description>{description}</Description>}
          {uspList && (
            <Usp>
              <UspList small items={uspList} />
            </Usp>
          )}
        </ContentBody>
        <ContentFooter>
          <Price>
            <PriceCell>
              {oldPrice ? (
                <OldPrice>{oldPrice}</OldPrice>
              ) : (
                <Label>{i18next.t('productCard.price')}</Label>
              )}
              <CurrentPrice>{price}</CurrentPrice>
            </PriceCell>
            {deductibleVat && (
              <PriceCell>
              <Label>{i18next.t('productCard.priceExclVat')}</Label>
              <CurrentPrice>{deductibleVat}</CurrentPrice>
            </PriceCell>
            )}
            {leasingPrice && (
              <PriceCell>
                <Label>{i18next.t('productCard.privateLeasing')}</Label>
                <CurrentPrice>{leasingPrice}</CurrentPrice>
              </PriceCell>
            )}
          </Price>
        </ContentFooter>
      </Content>
    </Wrapper>
  );
};

export default ProductCard;
